import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useConfig } from "../../utensils/ConfigContext";

const DAYS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

// Normaliza " mierColes " -> "Miércoles"
function normalizeDay(s) {
  if (!s) return "";
  const clean = s.trim().toLowerCase();
  for (const d of DAYS) if (d.toLowerCase() === clean) return d;
  return s.trim();
}

// "7:00" -> "07:00" (para comparar)
function normTime(t = "") {
  const m = String(t || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "";
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  return `${h}:${m[2]}`;
}
function eqTime(a, b) {
  return normTime(a) === normTime(b);
}

/**
 * Parser estricto:
 * 1) Localiza el día en el string (case-insensitive).
 * 2) A partir de ese punto, toma la PRIMERA pareja HH:MM [–|-] HH:MM.
 *    (acepta en-dash/em-dash y espacios variables)
 * 3) Fallback: toma las dos primeras HH:MM si no encontró la pareja.
 */
function parseReserva(reservaStr) {
  if (typeof reservaStr !== "string") return { day: "", start: "", end: "" };

  // normaliza todos los guiones a "-"
  const unified = reservaStr.replace(/[—–−]/g, "-");

  // encontrar día y cortar desde ahí
  let dayFound = "";
  let afterDay = unified;
  for (const d of DAYS) {
    const re = new RegExp(`\\b${d}\\b`, "i");
    const m = unified.match(re);
    if (m) {
      dayFound = d;
      afterDay = unified.slice((m.index ?? 0) + m[0].length);
      break;
    }
  }

  // pareja HH:MM - HH:MM DESPUÉS del día
  const pair = afterDay.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
  if (pair) {
    return { day: dayFound, start: pair[1], end: pair[2] };
  }

  // fallback: dos primeras horas en todo el string
  const times = unified.match(/(\d{1,2}:\d{2})/g) || [];
  return {
    day: dayFound,
    start: times[0] || "",
    end: times[1] || "",
  };
}

/**
 * Props:
 *  - show, onHide
 *  - classId (string|number)
 *  - day (string) -> "Lunes" | ...
 *  - start? (string "HH:MM")
 *  - end?   (string "HH:MM")
 */
const SnackAttendanceModal = ({ show, onHide, classId, day, start, end }) => {
  const { apiToken } = useConfig();
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);

  useEffect(() => {
    const fetchAttendees = async () => {
      if (!classId || !day || !apiToken) return;
      setLoading(true);

      try {
        let allResults = [];
        let nextUrl =
          "https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&size=200";

        while (nextUrl) {
          const res = await fetch(nextUrl, {
            headers: { Authorization: `Token ${apiToken}` },
          });
          const data = await res.json();
          allResults = [...allResults, ...(data.results || [])];
          nextUrl = data.next;
        }

        const dayNorm = normalizeDay(day);

        const filtered = allResults.filter((r) => {
          // 1) Clase debe coincidir
          if (r?.Clase?.toString() !== classId?.toString()) return false;

          // 2) Parseo de la reserva
          const { day: d, start: s, end: e } = parseReserva(r?.Reserva);

          // 3) Día debe coincidir
          if (normalizeDay(d) !== dayNorm) return false;

          // 4) Si no se solicita hora, basta el día
          if (!start && !end) return true;

          // 5) Si se solicitan AMBAS horas, coincidencia estricta
          if (start && end) {
            return eqTime(s, start) && eqTime(e, end);
          }

          // 6) Si por alguna razón viene solo una
          if (start && !eqTime(s, start)) return false;
          if (end && !eqTime(e, end)) return false;

          return true;
        });

        setAttendees(filtered);
      } catch (err) {
        console.error("❌ Error al cargar asistentes:", err);
      } finally {
        setLoading(false);
      }
    };

    if (show) fetchAttendees();
  }, [show, classId, day, start, end, apiToken]);

  const subtitle =
    start && end
      ? `${day} ${normTime(start)}–${normTime(end)}`
      : day || "Asistencia";

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Lista de asistencia ({attendees.length}) — {subtitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <p>Cargando asistentes...</p>
        ) : attendees.length > 0 ? (
          <ul className="list-group">
            {attendees.map((a, index) => (
              <li
                key={a.id}
                className="list-group-item d-flex align-items-center"
              >
                <span className="me-2 text-muted fw-bold">#{index + 1}</span>
                <div>
                  <strong>{a.Nombre}</strong> — {a.Email}
                  {a.Reserva ? (
                    <div className="small text-muted">Reserva: {a.Reserva}</div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay asistentes registrados para esta franja.</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SnackAttendanceModal;
